// CLI project helpers (ARCHITECTURE.md §14).
//
// Bridges the agent-contracts DSL and the analyzer pipeline for the CLI:
// resolves the DSL to extract artifact `path_patterns` and a Navigation Index,
// then runs the AST → Dependency Graph stages. DSL resolution is best-effort —
// a project without contracts still yields a structural graph (with an empty
// artifact mapping) rather than aborting.

import { stat } from "node:fs/promises";
import { join } from "node:path";

import { resolve as resolveContracts, buildNavigationIndex } from "agent-contracts";

import { buildAstGraph } from "../ast/index.js";
import { buildDependencyGraph } from "../dependency/index.js";
import type { DependencyGraph } from "../dependency/types.js";
import { createDefaultRegistry } from "../analyzer/index.js";
import { loadCandidateEdges, openStore } from "../store/index.js";
import type { StoreHandle } from "../store/types.js";

/** Raised for recoverable analysis failures; mapped to CLI exit code 1. */
export class AnalysisError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalysisError";
  }
}

export interface ProjectContext {
  /** artifactId → glob patterns from the DSL (empty when no DSL resolves). */
  artifactPatterns: Map<string, string[]>;
  /** agent-contracts Navigation Index, when a DSL resolved. */
  navigationIndex?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Extract artifactId → path_patterns from either a Navigation Index
 * (`artifacts[id].files.path_patterns`) or a raw DSL (`artifacts[id]
 * .path_patterns`). Non-string patterns and pattern-less artifacts are dropped.
 */
function extractArtifactPatterns(
  source: Record<string, unknown> | undefined,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  const artifacts = source?.["artifacts"];
  if (!isRecord(artifacts)) return map;

  for (const [id, node] of Object.entries(artifacts)) {
    if (!isRecord(node)) continue;

    let raw: unknown;
    const files = node["files"];
    if (isRecord(files) && Array.isArray(files["path_patterns"])) {
      raw = files["path_patterns"];
    } else if (Array.isArray(node["path_patterns"])) {
      raw = node["path_patterns"];
    }

    const patterns = Array.isArray(raw)
      ? raw.filter((p): p is string => typeof p === "string")
      : [];
    if (patterns.length > 0) map.set(id, patterns);
  }
  return map;
}

/**
 * Resolve the agent-contracts DSL for a project. Best-effort: if no DSL can be
 * resolved (or it fails to compile), returns an empty artifact mapping so the
 * structural pipeline still runs.
 */
export async function loadProjectContext(
  projectRoot: string,
  dslPath?: string,
): Promise<ProjectContext> {
  let data: Record<string, unknown> | undefined;
  try {
    const result = await resolveContracts(dslPath ?? projectRoot);
    data = result.data;
  } catch {
    return { artifactPatterns: new Map(), navigationIndex: undefined };
  }

  let navigationIndex: Record<string, unknown> | undefined;
  try {
    navigationIndex = buildNavigationIndex(
      data as Parameters<typeof buildNavigationIndex>[0],
    ) as unknown as Record<string, unknown>;
  } catch {
    navigationIndex = undefined;
  }

  const artifactPatterns = extractArtifactPatterns(navigationIndex ?? data);
  return { artifactPatterns, navigationIndex };
}

/** Return the path to a root `package.json` manifest, if one exists. */
async function findManifests(projectRoot: string): Promise<string[]> {
  const manifest = join(projectRoot, "package.json");
  try {
    const info = await stat(manifest);
    return info.isFile() ? [manifest] : [];
  } catch {
    return [];
  }
}

export interface BuildGraphResult {
  graph: DependencyGraph;
  navigationIndex?: Record<string, unknown>;
  store?: StoreHandle;
  /** File contents loaded during AST build (avoids re-reading in context-pack). */
  fileContents: Map<string, string>;
}

/**
 * Run the AST → Dependency Graph stages for a project. Opens the persistent
 * store when caching is enabled; the caller owns closing the returned store.
 */
export async function buildProjectGraph(options: {
  projectRoot: string;
  dslPath?: string;
  useCache: boolean;
}): Promise<BuildGraphResult> {
  const { projectRoot, dslPath, useCache } = options;

  const t0 = performance.now();
  const { artifactPatterns, navigationIndex } = await loadProjectContext(
    projectRoot,
    dslPath,
  );
  const t1 = performance.now();

  const store = useCache ? await openStore() : undefined;
  const registry = createDefaultRegistry();

  const { graph: ast, fileContents } = await buildAstGraph({
    projectRoot,
    artifactPatterns,
    registry,
    store,
  });
  const t2 = performance.now();

  const manifests = await findManifests(projectRoot);
  const graph = await buildDependencyGraph({
    ast,
    projectRoot,
    artifactPatterns,
    manifests,
  });

  if (store) {
    const candidateEdges = loadCandidateEdges(store);
    if (candidateEdges.length > 0) {
      graph.edges.push(...candidateEdges);
    }
  }

  const t3 = performance.now();

  console.error(`[perf] loadProjectContext: ${(t1 - t0).toFixed(0)}ms`);
  console.error(`[perf] buildAstGraph: ${(t2 - t1).toFixed(0)}ms`);
  console.error(`[perf] buildDependencyGraph: ${(t3 - t2).toFixed(0)}ms`);
  console.error(`[perf] buildProjectGraph total: ${(t3 - t0).toFixed(0)}ms`);

  return { graph, navigationIndex, store, fileContents };
}
